/**
 * What TikTok's `POST /v2/post/publish/creator_info/query/` tells us about the
 * account a post will go to. Pure so the browser editor, the readiness layer and
 * the edge functions read one shape.
 *
 * Official reference (read 2026-11-03):
 * https://developers.tiktok.com/doc/content-posting-api-reference-query-creator-info
 * Audit UX rules that depend on it:
 * https://developers.tiktok.com/doc/content-sharing-guidelines
 *
 * The result is stored on `user_connections.metadata.tiktokCreatorInfo` and
 * refreshed each time the editor opens a TikTok post (the guidelines ask for a
 * fresh query every time the posting page renders; the endpoint allows 20 a
 * minute). A stored copy is only what the editor shows while the refresh runs,
 * and what scheduled posts are checked against.
 */

import type { TikTokPostSettings, TikTokPrivacyLevel } from './tiktok-post-params.ts';

export interface TikTokCreatorInfo {
  nickname: string | null;
  username: string | null;
  avatarUrl: string | null;
  /** Exactly what TikTok offers this creator. Empty means unknown. */
  privacyLevelOptions: TikTokPrivacyLevel[];
  commentDisabled: boolean;
  duetDisabled: boolean;
  stitchDisabled: boolean;
  /** The longest video this creator can post, in seconds. */
  maxVideoPostDurationSec: number | null;
  /** ISO time of the query that produced this copy. */
  fetchedAt: string | null;
}

const PRIVACY: readonly string[] = [
  'PUBLIC_TO_EVERYONE',
  'MUTUAL_FOLLOW_FRIENDS',
  'FOLLOWER_OF_CREATOR',
  'SELF_ONLY',
];

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/**
 * Reads the provider's `data` object (snake_case) or a stored copy
 * (camelCase) into the app shape. Returns `null` when there is nothing usable.
 */
export function normalizeTikTokCreatorInfo(
  raw: unknown,
  fetchedAt: string | null = null,
): TikTokCreatorInfo | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const optionsRaw = r.privacy_level_options ?? r.privacyLevelOptions;
  const options = Array.isArray(optionsRaw)
    ? (optionsRaw.filter(
        (option): option is TikTokPrivacyLevel =>
          typeof option === 'string' && PRIVACY.includes(option),
      ) as TikTokPrivacyLevel[])
    : [];
  const flag = (snake: string, camel: string) => r[snake] === true || r[camel] === true;
  const durationRaw = r.max_video_post_duration_sec ?? r.maxVideoPostDurationSec;
  const nickname = str(r.creator_nickname ?? r.nickname);
  if (options.length === 0 && !nickname) return null;
  return {
    nickname,
    username: str(r.creator_username ?? r.username),
    avatarUrl: str(r.creator_avatar_url ?? r.avatarUrl),
    privacyLevelOptions: options,
    commentDisabled: flag('comment_disabled', 'commentDisabled'),
    duetDisabled: flag('duet_disabled', 'duetDisabled'),
    stitchDisabled: flag('stitch_disabled', 'stitchDisabled'),
    maxVideoPostDurationSec:
      typeof durationRaw === 'number' && Number.isFinite(durationRaw) && durationRaw > 0
        ? durationRaw
        : null,
    fetchedAt: str(r.fetchedAt) ?? fetchedAt,
  };
}

/** The longest video Mora will send: the creator's own limit, never above the
 * API's 10 minutes. */
export const TIKTOK_API_MAX_VIDEO_SECONDS = 600;

export function tiktokMaxVideoSeconds(info: TikTokCreatorInfo | null | undefined): number {
  const own = info?.maxVideoPostDurationSec;
  return typeof own === 'number' && own > 0
    ? Math.min(own, TIKTOK_API_MAX_VIDEO_SECONDS)
    : TIKTOK_API_MAX_VIDEO_SECONDS;
}

/**
 * Applies what the creator disabled in TikTok itself: an interaction they turned
 * off there can never be switched on by a post, so it is forced off here too.
 */
export function lockSettingsToCreator(
  settings: TikTokPostSettings,
  info: TikTokCreatorInfo | null | undefined,
): TikTokPostSettings {
  if (!info) return settings;
  return {
    ...settings,
    allowComment: settings.allowComment && !info.commentDisabled,
    allowDuet: settings.allowDuet && !info.duetDisabled,
    allowStitch: settings.allowStitch && !info.stitchDisabled,
  };
}
